/**
 * Protected Route Component
 *
 * Wrapper component that checks if user has permission to access a route.
 * Uses permissionStore to check functionId-based permissions.
 * Redirects to /403 if user lacks required permissions.
 *
 * If no functionId is provided, it will automatically determine the functionId
 * from the current route path using the menuList from backend.
 *
 * @example
 * ```tsx
 * // In routes.tsx - Auto-detect from path
 * {
 *   path: '/users',
 *   element: (
 *     <ProtectedRoute>
 *       <UserManagement />
 *     </ProtectedRoute>
 *   ),
 * }
 * // Or explicit functionId
 * {
 *   path: '/users',
 *   element: (
 *     <ProtectedRoute functionId="USER">
 *       <UserManagement />
 *     </ProtectedRoute>
 *   ),
 * }
 * ```
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, usePermissionStore } from '@/store';
import type { MenuFunctionItem } from '@/store/permissionStore';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { logger } from '@/shared/utils/logger';
import { getFunctionIdFromPath } from '@/shared/utils/routeFunctionMapper';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Function ID to check permission for (e.g., 'USER', 'ROLE', 'CUSTOMER') */
  functionId?: string;
  /** Multiple function IDs - user needs ANY of these permissions */
  functionIds?: string[];
  /** Legacy: array of required permissions (deprecated, use functionId) */
  requiredPermissions?: string[];
  /** Legacy: required path (deprecated) */
  requiredPath?: string;
}

interface PermissionContext {
  user: { userId: string; userName?: string } | null;
  isInitialized: boolean;
  hasPermission: (functionId: string) => boolean;
  hasAnyPermission: (functionIds: string[]) => boolean;
  allowedFunctions: string[];
  menuList: MenuFunctionItem[];
  location: { pathname: string };
}

/**
 * Check if user is initialized
 */
const shouldWaitForInitialization = (
  user: PermissionContext['user'],
  isInitialized: boolean,
  location: PermissionContext['location'],
): boolean => {
  if (user && !isInitialized) {
    logger.debug('User authenticated but not initialized, waiting...', {
      userId: user.userId,
      path: location.pathname,
    });
    return true;
  }
  return false;
};

/**
 * Get effective functionId (either provided or auto-detected)
 */
const getEffectiveFunctionId = (
  functionId: string | undefined,
  functionIds: string[] | undefined,
  location: PermissionContext['location'],
  menuList: PermissionContext['menuList'],
): string | undefined => {
  if (functionId || functionIds) {
    return functionId;
  }

  const detected = getFunctionIdFromPath(location.pathname, menuList);

  if (detected) {
    logger.debug('Auto-detected functionId from path', {
      path: location.pathname,
      functionId: detected,
    });
  }

  return detected ?? undefined;
};

/**
 * Check single functionId permission
 */
const checkFunctionIdPermission = (
  functionId: string,
  hasPermission: PermissionContext['hasPermission'],
  user: PermissionContext['user'],
  location: PermissionContext['location'],
  allowedFunctions: string[],
): React.ReactElement | null => {
  // Dashboard always accessible to authenticated users
  if (functionId.toUpperCase() === 'DASHBOARD') {
    return null; // null means allow access
  }

  const permitted = hasPermission(functionId);

  if (!permitted) {
    logger.warn('Access denied - No permission for function', {
      userId: user?.userId,
      functionId,
      path: location.pathname,
      allowedFunctions,
    });
    return <Navigate to="/403" state={{ from: location }} replace />;
  }

  return null; // Allow access
};

/**
 * Check multiple functionIds permission (user needs ANY)
 */
const checkFunctionIdsPermission = (
  functionIds: string[],
  hasAnyPermission: PermissionContext['hasAnyPermission'],
  user: PermissionContext['user'],
  location: PermissionContext['location'],
): React.ReactElement | null => {
  if (!hasAnyPermission(functionIds)) {
    logger.warn('Access denied - No permission for any function', {
      userId: user?.userId,
      functionIds,
      path: location.pathname,
    });
    return <Navigate to="/403" state={{ from: location }} replace />;
  }

  return null; // Allow access
};

/**
 * Check legacy permissions
 */
const checkLegacyPermissions = (
  requiredPermissions: string[],
  requiredPath: string | undefined,
  hasAnyPermission: PermissionContext['hasAnyPermission'],
  user: PermissionContext['user'],
): React.ReactElement | null => {
  // If no legacy permissions required, allow access
  if (requiredPermissions.length === 0 && !requiredPath) {
    return null;
  }

  // Legacy path check (backward compatibility)
  if (requiredPath) {
    // Allow for backward compatibility
  }

  // Legacy permission check
  if (
    requiredPermissions.length > 0 &&
    !hasAnyPermission(requiredPermissions)
  ) {
    logger.warn('Access denied - Missing permissions', {
      userId: user?.userId,
      required: requiredPermissions,
    });
    return <Navigate to="/403" replace />;
  }

  return null; // Allow access
};

/**
 * Checks if user has required permissions to access a route
 * Returns 403 Forbidden if access is denied
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  functionId,
  functionIds,
  requiredPermissions = [],
  requiredPath,
}) => {
  const { user, isInitialized } = useAuthStore();
  const permissions = usePermissionStore();
  const { hasPermission, hasAnyPermission, allowedFunctions } =
    usePermissions();
  const location = useLocation();

  // Wait for initialization if needed
  if (shouldWaitForInitialization(user, isInitialized, location)) {
    return <>{children}</>;
  }

  // Get effective functionId
  const effectiveFunctionId = getEffectiveFunctionId(
    functionId,
    functionIds,
    location,
    permissions.menuList,
  );

  // Check functionId permission
  if (effectiveFunctionId) {
    const deniedElement = checkFunctionIdPermission(
      effectiveFunctionId,
      hasPermission,
      user,
      location,
      allowedFunctions,
    );
    if (deniedElement) {
      return deniedElement;
    }
    return <>{children}</>;
  }

  // Check functionIds permission
  if (functionIds && functionIds.length > 0) {
    const deniedElement = checkFunctionIdsPermission(
      functionIds,
      hasAnyPermission,
      user,
      location,
    );
    if (deniedElement) {
      return deniedElement;
    }
    return <>{children}</>;
  }

  // Check legacy permissions
  const deniedElement = checkLegacyPermissions(
    requiredPermissions,
    requiredPath,
    hasAnyPermission,
    user,
  );
  if (deniedElement) {
    return deniedElement;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
